import React from 'react';
import {Check, ArrowLeft} from 'lucide-react';
import {useSearchParams, useNavigate} from 'react-router-dom';

import {useCompleteReview} from '../net/query/CompleteReview.gql';
import {Button} from '../components/ui/button';
import {useToast} from '../components/ui/toast';
import {Card, CardContent, CardHeader, CardTitle} from '../components/ui/card';

/**
 * Review control component that appears in the chat sidebar when reviewing.
 * Only shows when the 'r' query parameter is present.
 */
export const ReviewControl = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {showToast} = useToast();
  const [completeReview, {loading: completingReview}] = useCompleteReview();

  const reviewEntryId = searchParams.get('r');

  // Only show if we have a review entry ID
  if (!reviewEntryId) {
    return null;
  }

  const handleCompleteReview = async () => {
    try {
      await completeReview({
        variables: {
          reviewQueueEntryId: reviewEntryId,
        },
      });

      showToast('Review completed successfully', 'success');

      // Navigate back to review page with the same 'r' parameter
      // This will highlight the entry and show the user where they were
      navigate(`/review?r=${encodeURIComponent(reviewEntryId)}`);
    } catch (error) {
      showToast('Failed to complete review', 'error');
      console.error('Error completing review:', error);
    }
  };

  const handleBack = () => {
    // Navigate back to review page with the same 'r' parameter
    // This will highlight the entry and show the user where they were
    navigate(`/review?r=${encodeURIComponent(reviewEntryId)}`);
  };

  return (
    <div className="p-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Check className="h-5 w-5" />
            Review
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            You are currently reviewing this conversation thread.
          </p>

          <div className="space-y-2">
            <Button
              onClick={handleCompleteReview}
              disabled={completingReview}
              className="w-full"
              size="sm"
            >
              <Check className="h-4 w-4 mr-2" />
              {completingReview ? 'Completing...' : 'Complete Review'}
            </Button>

            <Button
              variant="outline"
              onClick={handleBack}
              className="w-full"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Review Queue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
